import { useMutation, useQueryClient } from '@tanstack/react-query'
import { client } from '../client'
import { queryKeys } from '../keys'
import type { components } from '@/lib/generated/api-types'

type SearchParams = components['schemas']['SearchParams']
type LoopIntervalsRequest = components['schemas']['LoopIntervalsRequest']

// Start vehicle search
export function useStartSearch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      accountName,
      params,
    }: {
      accountName: string
      params: SearchParams
    }) => {
      const { data, error } = await client.POST(
        '/api/v1/accounts/{account_name}/searches',
        {
          params: { path: { account_name: accountName } },
          body: params,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: (_data, { accountName }) => {
      // Invalidate search status to start polling
      queryClient.invalidateQueries({
        queryKey: queryKeys.searches.current(accountName),
      })
    },
  })
}

// Update loop intervals
export function useUpdateLoopIntervals() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: LoopIntervalsRequest) => {
      const { data, error } = await client.PUT(
        '/api/v1/search/loop-intervals',
        {
          body: params,
        },
      )

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.searches.loopIntervals(), data)
    },
  })
}

// Stop current search
export function useStopSearch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ accountName }: { accountName: string }) => {
      const { error } = await client.DELETE(
        '/api/v1/accounts/{account_name}/searches/current',
        {
          params: { path: { account_name: accountName } },
        },
      )
      if (error) throw error
    },
    onSuccess: (_data, { accountName }) => {
      // Remove search from cache
      queryClient.removeQueries({
        queryKey: queryKeys.searches.current(accountName),
      })
    },
  })
}
